/** Renders the polished QEP Assistant and connects its in-memory conversation to the authenticated qep-mcp chat API. */
import React, {
  useEffect, useRef, useState,
} from 'react';
import ReactMarkdown from 'react-markdown';
import superagent from 'superagent';
import {
  Bot, Check, Copy, MessageCircle, RotateCcw, Send, Sparkles, User, X,
} from 'lucide-react';
import {getApiServer} from '@qep/shared/src/utils/runtime-config';
import './chat.css';

const chatRoute = `${getApiServer()}/qep-mcp/v1/chat/completions`;
const chatModel = 'qep-assistant';
const maximumPromptLength = 2000;

type ChatIconPosition = {x: number; y: number};
type ChatMessageRole = 'user' | 'assistant';
type ChatMessageStatus = 'complete' | 'loading' | 'error';
type ChatMessage = {id: number; role: ChatMessageRole; content: string; status: ChatMessageStatus};
type ChatCompletionMessage = {role: ChatMessageRole; content: string};

/** Converts completed rendered turns plus one prompt into the provider-neutral chat message contract. */
function buildChatCompletionMessages(history: ChatMessage[], prompt: string): ChatCompletionMessage[] {
  const completionMessages: ChatCompletionMessage[] = [];
  for (const message of history) {
    if (message.status === 'complete') {
      completionMessages.push({role: message.role, content: message.content});
    }
  }

  completionMessages.push({role: 'user', content: prompt});
  return completionMessages;
}

/** Narrows an unknown value to a non-array record for response-boundary validation. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Maps a trusted HTTP status to safe user text without rendering provider-controlled error payloads. */
function readChatErrorMessage(error: unknown): string {
  if (isRecord(error) && error.status === 429) {
    return 'Error: The chatbot is rate limited. Please wait a moment and try again.';
  }

  return 'Error: Unable to fetch response.';
}

/** Narrows an unknown value to an array whose entries remain untrusted. */
function isUnknownArray(value: unknown): value is unknown[] {
  return Array.isArray(value);
}

/** Extracts non-empty assistant text from an untrusted OpenAI-compatible completion response. */
function readAssistantContent(value: unknown): string | undefined {
  if (!isRecord(value) || !isUnknownArray(value.choices)) {
    return undefined;
  }

  const firstChoice = value.choices[0];
  if (!isRecord(firstChoice) || !isRecord(firstChoice.message)) {
    return undefined;
  }

  const {content} = firstChoice.message;
  return typeof content === 'string' && content.trim() ? content : undefined;
}

/** Parses one Chat storage entry, removing malformed JSON so later mounts can recover. */
function readStoredJson(key: string): unknown | undefined {
  const storedValue = localStorage.getItem(key);
  if (!storedValue) {
    return undefined;
  }

  try {
    return JSON.parse(storedValue) as unknown;
  } catch {
    localStorage.removeItem(key);
    return undefined;
  }
}

/** Checks that persisted icon coordinates are finite numbers before viewport constraints are applied. */
function isChatIconPosition(value: unknown): value is ChatIconPosition {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const position = value as {x?: unknown; y?: unknown};
  return typeof position.x === 'number' && Number.isFinite(position.x)
    && typeof position.y === 'number' && Number.isFinite(position.y);
}

/** Constrains a launcher position to the visible viewport and returns safe coordinates. */
function constrainChatIconPosition(position: ChatIconPosition): ChatIconPosition {
  const maximumX = Math.max(0, window.innerWidth - 70);
  const maximumY = Math.max(0, window.innerHeight - 70);
  return {
    x: Math.max(0, Math.min(position.x, maximumX)),
    y: Math.max(0, Math.min(position.y, maximumY)),
  };
}

/** Calculates a responsive panel position beside the launcher while keeping the entire panel visible. */
function calculateChatWindowPosition(iconPosition: ChatIconPosition): ChatIconPosition {
  const panelWidth = Math.min(420, window.innerWidth - 24);
  const panelHeight = Math.min(640, window.innerHeight - 24);
  const iconSize = 52;
  const gap = 12;
  const edge = 12;
  const spaceAbove = iconPosition.y;
  const spaceLeft = iconPosition.x;
  const top = spaceAbove >= panelHeight + gap
    ? iconPosition.y - panelHeight - gap
    : Math.min(iconPosition.y + iconSize + gap, window.innerHeight - panelHeight - edge);
  const left = spaceLeft >= panelWidth + gap
    ? iconPosition.x - panelWidth - gap
    : Math.min(iconPosition.x + iconSize + gap, window.innerWidth - panelWidth - edge);

  return {x: Math.max(edge, left), y: Math.max(edge, top)};
}

/** Replaces one message by identifier without mutating React state or depending on array ordering. */
function replaceChatMessage(history: ChatMessage[], messageId: number, replacement: ChatMessage): ChatMessage[] {
  const updatedHistory: ChatMessage[] = [];
  for (const message of history) {
    updatedHistory.push(message.id === messageId ? replacement : message);
  }

  return updatedHistory;
}

/** Reports whether the rendered conversation currently contains an in-flight assistant turn. */
function hasLoadingMessage(history: ChatMessage[]): boolean {
  for (const message of history) {
    if (message.status === 'loading') {
      return true;
    }
  }

  return false;
}

/** Renders the draggable Browser assistant while persisting only its non-sensitive launcher position. */
const Chat: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [copiedMessageId, setCopiedMessageId] = useState<number>();
  const [copyErrorMessageId, setCopyErrorMessageId] = useState<number>();
  const nextMessageId = useRef(1);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const loading = hasLoadingMessage(messages);

  const [iconPosition, setIconPosition] = useState(() => {
    const savedPosition = readStoredJson('chatIconPosition');
    const initialPosition = isChatIconPosition(savedPosition)
      ? savedPosition
      : {x: window.innerWidth - 70, y: window.innerHeight - 70};
    return constrainChatIconPosition(initialPosition);
  });

  /** Toggles the assistant panel without clearing its session-local conversation. */
  const toggleChat = () => {
    setIsOpen(previousIsOpen => !previousIsOpen);
  };

  useEffect(() => {
    // Conversation data from earlier builds is removed so another authenticated user cannot inherit it.
    localStorage.removeItem('chatMessages');
  }, []);

  useEffect(() => {
    /** Keeps the launcher reachable when the browser viewport changes size. */
    const handleResize = () => {
      setIconPosition(previousPosition => constrainChatIconPosition(previousPosition));
    };

    /** Removes the viewport listener when the assistant unmounts. */
    const removeResizeListener = () => {
      window.removeEventListener('resize', handleResize);
    };

    window.addEventListener('resize', handleResize);
    return removeResizeListener;
  }, []);

  useEffect(() => {
    // Launcher placement is non-sensitive and survives reloads independently of chat text.
    localStorage.setItem('chatIconPosition', JSON.stringify(iconPosition));
  }, [iconPosition]);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    /** Closes the panel from anywhere in the window while leaving its conversation intact. */
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    /** Removes the keyboard listener whenever the panel closes or unmounts. */
    const removeEscapeListener = () => {
      globalThis.removeEventListener('keydown', handleEscape);
    };

    globalThis.addEventListener('keydown', handleEscape);
    return removeEscapeListener;
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && messages.length > 0) {
      messagesEndRef.current?.scrollIntoView?.({behavior: 'smooth', block: 'end'});
    }
  }, [isOpen, messages]);

  /** Opens or closes the launcher from Enter or Space while preserving mouse drag behavior. */
  const handleLauncherKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleChat();
    }
  };

  /** Tracks a launcher mouse gesture, moving on drag or toggling the panel on a stationary click. */
  const handleDragStart = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (event.button !== 0) {
      return;
    }

    let isDragging = false;
    const startX = event.clientX - iconPosition.x;
    const startY = event.clientY - iconPosition.y;
    const overlay = document.createElement('div');
    overlay.className = 'chat-drag-overlay';
    document.body.append(overlay);

    /** Moves the launcher with the pointer and constrains it to the visible viewport. */
    const handleDragMove = (moveEvent: MouseEvent) => {
      isDragging = true;
      setIconPosition(constrainChatIconPosition({
        x: moveEvent.clientX - startX,
        y: moveEvent.clientY - startY,
      }));
    };

    /** Cleans up document listeners and treats a no-movement gesture as launcher activation. */
    const handleDragEnd = () => {
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
      overlay.remove();
      if (!isDragging) {
        toggleChat();
      }
    };

    document.addEventListener('mousemove', handleDragMove);
    document.addEventListener('mouseup', handleDragEnd);
  };

  /** Sends one credentialed non-streaming completion and resolves its placeholder with safe text. */
  const getChatResponse = async (completionMessages: ChatCompletionMessage[], responseMessageId: number) => {
    try {
      const chatResponse = await superagent
        .post(chatRoute)
        .withCredentials()
        .send({model: chatModel, messages: completionMessages})
        .set('Accept', 'application/json');
      const chatResponseContent = readAssistantContent(chatResponse.body as unknown);
      if (!chatResponseContent) {
        throw new Error('Invalid chat completion response');
      }

      const replacement: ChatMessage = {
        id: responseMessageId,
        role: 'assistant',
        content: chatResponseContent,
        status: 'complete',
      };
      setMessages(previousMessages => replaceChatMessage(previousMessages, responseMessageId, replacement));
    } catch (error: unknown) {
      console.error('Error in chat request');
      const replacement: ChatMessage = {
        id: responseMessageId,
        role: 'assistant',
        content: readChatErrorMessage(error),
        status: 'error',
      };
      setMessages(previousMessages => replaceChatMessage(previousMessages, responseMessageId, replacement));
    }
  };

  /** Queues one trimmed prompt and starts a completion using history captured before submission. */
  const submitPrompt = (requestedPrompt = input) => {
    const prompt = requestedPrompt.trim();
    if (!prompt || loading) {
      return;
    }

    const userMessage: ChatMessage = {
      id: nextMessageId.current,
      role: 'user',
      content: prompt,
      status: 'complete',
    };
    nextMessageId.current += 1;
    const assistantMessage: ChatMessage = {
      id: nextMessageId.current,
      role: 'assistant',
      content: '',
      status: 'loading',
    };
    nextMessageId.current += 1;
    const completionMessages = buildChatCompletionMessages(messages, prompt);
    setMessages(previousMessages => [...previousMessages, userMessage, assistantMessage]);
    setInput('');
    setCopiedMessageId(undefined);
    setCopyErrorMessageId(undefined);
    void getChatResponse(completionMessages, assistantMessage.id);
  };

  /** Submits the controlled composer while preventing browser form navigation. */
  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submitPrompt();
  };

  /** Synchronizes the controlled composer with user-entered text up to its declared limit. */
  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(event.target.value);
  };

  /** Sends on unmodified Enter while preserving Shift+Enter and IME composition for multiline text. */
  const handleComposerKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      submitPrompt();
    }
  };

  /** Submits the visible suggestion stored on its button through the normal prompt flow. */
  const handleSuggestedPrompt = (event: React.MouseEvent<HTMLButtonElement>) => {
    const {prompt} = event.currentTarget.dataset;
    if (prompt) {
      submitPrompt(prompt);
    }
  };

  /** Clears the in-memory conversation after confirmation and returns focus to the composer. */
  const handleNewChat = () => {
    // Browser confirmation prevents accidental loss of the intentionally non-persisted conversation.
    // eslint-disable-next-line no-alert
    if (messages.length > 0 && !globalThis.confirm('Start a new chat? This conversation will be cleared.')) {
      return;
    }

    setMessages([]);
    setInput('');
    setCopiedMessageId(undefined);
    setCopyErrorMessageId(undefined);
    inputRef.current?.focus();
  };

  /** Copies one completed assistant response and records success or failure for accessible feedback. */
  const copyAssistantResponse = async (messageId: string) => {
    let selectedMessage: ChatMessage | undefined;
    for (const message of messages) {
      if (String(message.id) === messageId && message.role === 'assistant' && message.status === 'complete') {
        selectedMessage = message;
        break;
      }
    }

    if (!selectedMessage) {
      return;
    }

    try {
      await navigator.clipboard.writeText(selectedMessage.content);
      setCopiedMessageId(selectedMessage.id);
      setCopyErrorMessageId(undefined);
    } catch {
      setCopyErrorMessageId(selectedMessage.id);
      setCopiedMessageId(undefined);
    }
  };

  /** Starts the asynchronous clipboard action for the assistant message identified by the clicked control. */
  const handleCopyResponse = (event: React.MouseEvent<HTMLButtonElement>) => {
    const {messageId} = event.currentTarget.dataset;
    if (messageId) {
      void copyAssistantResponse(messageId);
    }
  };

  /** Builds the visible conversation rows with role identity, Markdown, loading state, and copy feedback. */
  const renderMessages = (): React.ReactNode[] => {
    const renderedMessages: React.ReactNode[] = [];
    for (const message of messages) {
      const isAssistant = message.role === 'assistant';
      const isCopied = copiedMessageId === message.id;
      const hasCopyError = copyErrorMessageId === message.id;
      let messageContent: React.ReactNode;
      if (message.status === 'loading') {
        messageContent = (
          <div className='chat-thinking' role='status' aria-live='polite'>
            <span>QEP Assistant is thinking</span>
            <span className='chat-thinking-dots' aria-hidden='true'><i/><i/><i/></span>
          </div>
        );
      } else if (isAssistant) {
        messageContent = <ReactMarkdown>{message.content}</ReactMarkdown>;
      } else {
        messageContent = <p>{message.content}</p>;
      }

      let copyButtonText = 'Copy';
      if (isCopied) {
        copyButtonText = 'Copied';
      } else if (hasCopyError) {
        copyButtonText = 'Try again';
      }

      const renderedMessage = (
        <article className={`chat-message-row chat-message-row--${message.role}`} key={message.id}>
          <div className='chat-message-avatar' aria-hidden='true'>
            {isAssistant ? <Bot size={16}/> : <User size={16}/>}
          </div>
          <div className='chat-message-stack'>
            <span className='chat-message-author'>{isAssistant ? 'QEP Assistant' : 'You'}</span>
            <div className={`chat-message chat-message--${message.role} chat-message--${message.status}`}>
              {messageContent}
              {isAssistant && message.status === 'complete'
                ? (
                  <button
                    type='button'
                    className={`chat-copy-button${isCopied ? ' chat-copy-button--copied' : ''}`}
                    aria-label={isCopied ? 'Response copied' : 'Copy response'}
                    data-message-id={message.id}
                    onClick={handleCopyResponse}
                  >
                    {isCopied ? <Check size={14}/> : <Copy size={14}/>}
                    <span>{copyButtonText}</span>
                  </button>
                )
                : null}
            </div>
          </div>
        </article>
      );
      renderedMessages.push(renderedMessage);
    }

    return renderedMessages;
  };

  const chatWindowPosition = calculateChatWindowPosition(iconPosition);

  return (
    <div className='chat-container'>
      {isOpen
        ? (
          <section
            id='qep-assistant-dialog'
            className='chat-window'
            role='dialog'
            aria-modal='false'
            aria-labelledby='qep-assistant-title'
            style={{left: `${chatWindowPosition.x}px`, top: `${chatWindowPosition.y}px`}}
          >
            <header className='chat-header'>
              <div className='chat-brand-mark' aria-hidden='true'><Sparkles size={19}/></div>
              <div className='chat-heading'>
                <h2 id='qep-assistant-title'>QEP Assistant</h2>
                <span className='chat-presence'><i aria-hidden='true'/>Online</span>
              </div>
              <div className='chat-header-actions'>
                <button
                  type='button'
                  className='chat-header-button'
                  aria-label='Start a new chat'
                  disabled={loading}
                  onClick={handleNewChat}
                >
                  <RotateCcw size={17}/>
                </button>
                <button
                  type='button'
                  className='chat-header-button'
                  aria-label='Close QEP Assistant'
                  onClick={toggleChat}
                >
                  <X size={19}/>
                </button>
              </div>
            </header>

            <div className='chat-messages' aria-live='polite'>
              {messages.length === 0
                ? (
                  <div className='chat-welcome'>
                    <div className='chat-welcome-icon' aria-hidden='true'><Sparkles size={24}/></div>
                    <p className='chat-eyebrow'>Your QEP copilot</p>
                    <h3>How can I help?</h3>
                    <p className='chat-welcome-copy'>Ask about your positions, orders, or what needs attention across your workspace.</p>
                    <div className='chat-suggestions' aria-label='Suggested prompts'>
                      <button type='button' data-prompt='Summarize my positions' onClick={handleSuggestedPrompt}>
                        Summarize my positions
                      </button>
                      <button type='button' data-prompt='Show my open orders' onClick={handleSuggestedPrompt}>
                        Show my open orders
                      </button>
                      <button type='button' data-prompt='What needs my attention?' onClick={handleSuggestedPrompt}>
                        What needs my attention?
                      </button>
                    </div>
                  </div>
                )
                : renderMessages()}
              <div ref={messagesEndRef}/>
            </div>

            <form className='chat-form' onSubmit={handleFormSubmit}>
              <div className='chat-composer'>
                <label className='chat-visually-hidden' htmlFor='qep-assistant-input'>Message QEP Assistant</label>
                <textarea
                  id='qep-assistant-input'
                  ref={inputRef}
                  value={input}
                  onChange={handleInputChange}
                  onKeyDown={handleComposerKeyDown}
                  className='chat-input'
                  placeholder='Ask QEP Assistant...'
                  rows={2}
                  maxLength={maximumPromptLength}
                  disabled={loading}
                />
                <button
                  type='submit'
                  className='chat-submit'
                  aria-label='Send message'
                  disabled={loading || !input.trim()}
                >
                  <Send size={18}/>
                </button>
              </div>
              <div className='chat-composer-meta'>
                <span>Enter to send · Shift+Enter for a new line</span>
                <span>{input.length}/{maximumPromptLength}</span>
              </div>
              <p className='chat-disclaimer'>AI responses may be inaccurate. Verify important details.</p>
            </form>
          </section>
        )
        : null}
      <button
        type='button'
        className='chat-icon'
        aria-label={isOpen ? 'Close QEP Assistant' : 'Open QEP Assistant'}
        aria-expanded={isOpen}
        aria-controls='qep-assistant-dialog'
        title='QEP Assistant — drag to move'
        style={{left: `${iconPosition.x}px`, top: `${iconPosition.y}px`}}
        onMouseDown={handleDragStart}
        onKeyDown={handleLauncherKeyDown}
      >
        {isOpen ? <X size={23}/> : <MessageCircle size={24}/>}
        <span className='chat-icon-spark' aria-hidden='true'><Sparkles size={10}/></span>
      </button>
    </div>
  );
};

export default Chat;
